/**
 * Drop-in replacement for Amplify Storage, backed by GCS via backend signed URLs.
 *
 * Usage — replace:
 *   import { Storage } from 'aws-amplify';
 * with:
 *   import { GCSStorage as Storage } from '../storage/gcs';
 */
import { axios as api } from '../api';

interface GCSObject {
  key: string;
  size: number;
  lastModified: Date;
}

interface GetOptions {
  download?: boolean;
}

interface PutResult {
  key: string;
  [key: string]: string;
}

interface GetResult {
  Body: Blob;
}

export const GCSStorage = {
  // No-op: Amplify Storage.configure() adjusts S3 access levels; GCS uses signed URLs for all access.
  configure: (_config: unknown): void => {},

  async list(prefix: string): Promise<GCSObject[]> {
    const { data } = await api.get<GCSObject[]>('/v6/storage/list', { params: { prefix } });
    return data.map((obj) => ({ ...obj, lastModified: new Date(obj.lastModified) }));
  },

  async put(
    key: string,
    file: File | Blob,
    _config?: unknown,
    onProgress?: (progress: number) => void
  ): Promise<PutResult> {
    const contentType =
      (file instanceof File ? file.type : '') || 'application/octet-stream';
    const { data } = await api.post<{ url: string; key: string }>('/v6/storage/upload-url', {
      key,
      content_type: contentType,
    });
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', data.url);
      xhr.setRequestHeader('Content-Type', contentType);

      if (xhr.upload && onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const percent = Math.round((e.loaded / e.total) * 100);
            onProgress(percent);
          }
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ key: data.key });
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('Network error during file upload'));
      xhr.send(file);
    });
  },

  async get(key: string, options?: GetOptions): Promise<string | GetResult> {
    const { data } = await api.get<{ url: string }>('/v6/storage/url', { params: { key } });
    if (options?.download) {
      const resp = await fetch(data.url);
      const blob = await resp.blob();
      return { Body: blob };
    }
    return data.url;
  },

  async remove(key: string): Promise<void> {
    await api.delete('/v6/storage/object', { params: { key } });
  },

  async copy(source: { key: string }, dest: { key: string }): Promise<void> {
    await api.post('/v6/storage/copy', { source_key: source.key, dest_key: dest.key });
  },
};

/**
 * Convenience function for uploading a File to GCS with an auto-generated key.
 * Returns the GCS object key to store in the API.
 */
export async function uploadToGCS(file: File): Promise<string> {
  const { key } = await GCSStorage.put(
    `uploads/general/${Date.now()}_${file.name.replace(/[^\w\d_\-.]+/gi, '')}`,
    file,
  );
  return key;
}
